/**
 * Prompt Forge page-ID derivation.
 *
 * Every authenticated route gets a stable `PG-<CODE>` identifier from its
 * URL, without route authors having to remember to tag their component.
 * The AppLayout wrapper attaches `data-host-page data-page-id={derivePageId(pathname)}`
 * once; Prompt Forge's inference engine then picks it up for every pin
 * generated on that page.
 *
 * Strategy:
 *   1. OVERRIDES — pathname-specific short codes (e.g. `PG-LIST-MGMT` instead
 *      of the auto-derived `PG-LIST-MANAGEMENT`). Keep this list small.
 *   2. Exact label lookup — pillarDefs is the source of truth for
 *      `path → label`, so derived IDs stay in sync with the navigation menu.
 *      Adding a new item to pillarDefs gives it a page ID automatically.
 *   3. Longest-prefix + remainder — for dynamic routes like `/companies/:id`
 *      we find the longest registered prefix (`/companies`) and append:
 *       - `-DETAIL` (singular) when the remainder is an ID-like segment
 *       - `-<ACTION>` (plural kept) when an ID segment is followed by a
 *         named action, e.g. `/campaigns/:id/edit` → `PG-CAMPAIGNS-EDIT`
 *   4. Slug fallback — slugify the path so unknown routes still produce a
 *      stable ID (e.g. `/foo/bar` → `PG-FOO-BAR`).
 */
import {
    UNIVERSAL_TOP,
    UNIVERSAL_AI,
    UNIVERSAL_SUPPORT,
    BOTTOM_PINNED,
    PILLAR_DEFS,
} from '../config/pillarDefs';

const OVERRIDES = {
    '/list-management': 'PG-LIST-MGMT',
    '/super-admin': 'PG-SUPER-ADMIN',
};

function flattenPathLabelMap() {
    const map = {};
    const add = items => (items || []).forEach(it => {
        if (it?.path && it?.label) map[it.path] = it.label;
    });
    add(UNIVERSAL_TOP);
    add(UNIVERSAL_AI?.items);
    add(UNIVERSAL_SUPPORT?.items);
    add(BOTTOM_PINNED);
    (PILLAR_DEFS || []).forEach(pillar => {
        (pillar.sections || []).forEach(section => add(section.items));
    });
    return map;
}

const PATH_LABEL_MAP = flattenPathLabelMap();

function labelToCode(label) {
    return label
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

// Basic English pluralization reversal — good enough for the handful of
// plural route bases we have (Companies, Contacts, Deals, Forms, Events…).
function singular(code) {
    if (code.endsWith('IES')) return code.slice(0, -3) + 'Y';
    if (code.endsWith('SES')) return code.slice(0, -2);
    if (code.endsWith('S') && !code.endsWith('SS')) return code.slice(0, -1);
    return code;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MONGO_ID_RE = /^[0-9a-f]{24}$/i;
function looksLikeId(s) {
    if (!s) return false;
    return UUID_RE.test(s) || MONGO_ID_RE.test(s) || /^\d+$/.test(s);
}

function segmentToCode(s) {
    return s
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function deriveSuffix(baseCode, restSegments) {
    if (restSegments.length === 0) return 'PG-' + baseCode;
    const [next, ...rest] = restSegments;
    if (looksLikeId(next)) {
        if (rest.length === 0) {
            // /companies/:id → PG-COMPANY-DETAIL
            return 'PG-' + singular(baseCode) + '-DETAIL';
        }
        // /campaigns/:id/edit → PG-CAMPAIGNS-EDIT (plural kept for actions)
        return 'PG-' + baseCode + '-' + rest.map(segmentToCode).join('-');
    }
    // /forms/new → PG-FORMS-NEW
    return 'PG-' + baseCode + '-' + segmentToCode(next)
        + (rest.length ? '-' + rest.map(segmentToCode).join('-') : '');
}

export function derivePageId(pathname) {
    if (!pathname || pathname === '/') return 'PG-ROOT';
    const path = pathname.replace(/\/+$/, '') || '/';

    if (OVERRIDES[path]) return OVERRIDES[path];
    const directLabel = PATH_LABEL_MAP[path];
    if (directLabel) return 'PG-' + labelToCode(directLabel);

    // Find the longest registered prefix (override or pillarDefs label).
    // Fall back to the first URL segment so ID-detection still applies to
    // routes like `/deals/:id` that aren't in the navigation menu.
    const segments = path.split('/').filter(Boolean);
    let baseCode = null;
    let restIdx = 0;
    for (let i = segments.length - 1; i > 0; i--) {
        const prefix = '/' + segments.slice(0, i).join('/');
        if (OVERRIDES[prefix]) {
            baseCode = OVERRIDES[prefix].replace(/^PG-/, '');
            restIdx = i;
            break;
        }
        if (PATH_LABEL_MAP[prefix]) {
            baseCode = labelToCode(PATH_LABEL_MAP[prefix]);
            restIdx = i;
            break;
        }
    }
    if (baseCode === null) {
        baseCode = segmentToCode(segments[0]);
        restIdx = 1;
    }

    return deriveSuffix(baseCode, segments.slice(restIdx));
}

// Exported for tests / debugging.
export const __test__ = { PATH_LABEL_MAP, OVERRIDES, labelToCode, singular, looksLikeId };
